import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useAppSelector } from '@/hooks';
import { useTheme } from '@mui/material/styles';
import { renderComponent } from '@/utils';
import {
  loanTypeOptions,
  propertyStatusOptions,
  coBorrowerOptions,
} from './config/options';
import { getLoanSetupConfig } from './config';
import { useLoan } from '@/contexts';
import { LoanPurpose } from '@/config/enums';

/**
 * LoanSetup - Combined loan type and property status selection
 *
 * Flow:
 * 1. Initially shows "What type of loan do you need?" with Purchase/Refinance options
 * 2. When an option is selected, the card expands to show "Do you already have a property in mind?"
 * 3. Shows Yes/No options below the loan type selection
 * 4. Once both are selected, user can proceed to the next step
 *
 * This component uses a config-based architecture for flexibility and maintainability.
 */
export const LoanSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { loanSetupData, updateLoanSetup } = useLoan();

  // Get Redux state for building query params
  const loanApplication = useAppSelector((state) => state.loanApplication);

  // Initialize state from context
  const [selectedLoanType, setSelectedLoanType] = useState<string | null>(
    loanSetupData.loanType || null
  );
  const [selectedPropertyStatus, setSelectedPropertyStatus] = useState<
    string | null
  >(loanSetupData.propertyAvailable || null);
  const [selectedCoBorrower, setSelectedCoBorrower] = useState<string | null>(
    loanSetupData.coBorrower || null
  );

  const handleBack = () => {
    navigate('/quickloan');
  };

  const handleNext = () => {
    // Validation:
    // - Purchase: require loanType + propertyAvailable + coBorrower
    // - Refinance: require loanType + coBorrower
    const isPurchaseComplete =
      selectedLoanType === LoanPurpose.Purchase &&
      selectedPropertyStatus &&
      selectedCoBorrower;
    const isRefinanceComplete =
      selectedLoanType === LoanPurpose.RefinanceCashOut && selectedCoBorrower;
    const canProceed = isPurchaseComplete || isRefinanceComplete;

    if (canProceed) {
      // Log context data
      console.log('Loan Context Data:', {
        loanSetupData,
        currentSelections: {
          loanType: selectedLoanType,
          propertyAvailable: selectedPropertyStatus,
          coBorrower: selectedCoBorrower,
        },
      });

      // Navigate to signup page
      navigate('/quickloan/signup');
    }
  };

  const handleLoanTypeSelect = (value: string) => {
    setSelectedLoanType(value);

    // If refinance is selected, clear property status and co-borrower
    if (value === LoanPurpose.RefinanceCashOut) {
      setSelectedPropertyStatus(null);
      setSelectedCoBorrower(null);
      updateLoanSetup({
        loanType: value,
        propertyAvailable: undefined,
        coBorrower: undefined,
      });
    } else {
      // For purchase, only clear co-borrower if property status changes
      setSelectedCoBorrower(null);
      updateLoanSetup({
        loanType: value,
        coBorrower: undefined,
      });
    }
  };

  const handlePropertyStatusSelect = (value: string) => {
    setSelectedPropertyStatus(value);
    // Save to context immediately
    updateLoanSetup({
      propertyAvailable: value,
    });
  };

  const handleCoBorrowerSelect = (value: string) => {
    setSelectedCoBorrower(value);
    // Save to context immediately
    updateLoanSetup({
      coBorrower: value,
    });
  };

  const loanOptions = loanTypeOptions(t, theme);
  const propertyOptions = propertyStatusOptions(t, theme);
  const borrowerOptions = coBorrowerOptions(t, theme);

  const config = getLoanSetupConfig({
    theme,
    t,
    loanOptions,
    propertyOptions,
    coBorrowerOptions: borrowerOptions,
    selectedLoanType,
    selectedPropertyStatus,
    selectedCoBorrower,
    onSelectLoanType: handleLoanTypeSelect,
    onSelectPropertyStatus: handlePropertyStatusSelect,
    onSelectCoBorrower: handleCoBorrowerSelect,
    onBack: handleBack,
    onNext: handleNext,
  });

  return <>{renderComponent(config, 0, false)}</>;
};

export default LoanSetup;
